// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT
import { Component, OnInit, OnDestroy } from '@angular/core';
import { Observable, Subscription, BehaviorSubject } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { Store, select } from '@ngrx/store';
import { PolicyFormDetails, GoAction, PolicyService, QueueAlertAction, AlertType } from '@app/core';
import {
  State,
  LoadingPolicyAction,
  StatementSelections,
  selectPolicyFormDetails,
  selectPolicyStatementSelections,
  ClearPolicyFormAction,
  selectPolicyDraft
} from '../state';

@Component({
  selector: 'app-review-new-policy',
  templateUrl: './review-new-policy.component.html',
  styleUrls: ['./review-new-policy.component.scss']
})
export class ReviewNewPolicyComponent implements OnInit, OnDestroy {
  readonly statementSelectionRows$: Observable<{ [key: string]: string }[]>;
  readonly details$: Observable<PolicyFormDetails>;
  readonly columns = [{ prop: 'effect' }, { prop: 'service' }, { prop: 'resource' }, { prop: 'action' }];

  readonly isSubmitting$ = new BehaviorSubject<boolean>(false);
  readonly failedSubmit$ = new BehaviorSubject<boolean>(false);
  private submitSubscription: Subscription | undefined = undefined;

  constructor(private store: Store<State>, private policyService: PolicyService) {
    this.details$ = this.store.pipe(select(selectPolicyFormDetails));
    this.statementSelectionRows$ = this.store.pipe(
      select(selectPolicyStatementSelections),
      map(statements => statements.map(statement => this.statementToStatementRow(statement)))
    );
  }

  ngOnInit() {}

  ngOnDestroy() {
    if (this.submitSubscription !== undefined) {
      this.submitSubscription.unsubscribe();
    }
  }

  cancel() {
    this.store.dispatch(new ClearPolicyFormAction());
  }

  submit() {
    this.setSubmitting();
    const submit$ = this.store.pipe(
      select(selectPolicyDraft),
      switchMap(policy => this.policyService.create(policy))
    );
    this.submitSubscription = submit$.subscribe(
      _ => {
        this.store.dispatch(new ClearPolicyFormAction());
        this.store.dispatch(new GoAction(['/iam/policies']));
        this.store.dispatch(new LoadingPolicyAction());
        this.store.dispatch(
          new QueueAlertAction({
            alertText: `Policy has successfully been created.`,
            alertType: AlertType.SUCCESS
          })
        );
      },
      _ => this.failSubmitting()
    );
  }

  private setSubmitting() {
    this.isSubmitting$.next(true);
    this.failedSubmit$.next(false);
  }

  private failSubmitting() {
    this.isSubmitting$.next(false);
    this.failedSubmit$.next(true);
  }

  private statementToStatementRow(statement: StatementSelections) {
    const defaultSelection = { name: '' };
    const { service = defaultSelection, resource = defaultSelection, actions = statement.actions } = statement;

    const actionsArr: string[] = [];
    let actionsStr = '';
    if (actions) {
      actions.forEach(e => {
        actionsArr.push(e.name.toString());
      });
      actionsStr = actionsArr.join(',');
    }

    return {
      effect: statement.effect || '',
      service: service.name,
      resource: resource.name,
      action: actionsStr
    };
  }
}



// WEBPACK FOOTER //
// ./src/app/access-management/review-new-policy/review-new-policy.component.ts